<template>
  <form @submit.prevent>
    <div
      v-show="!isWaitingListMessage"
      class="vx-row">
      <div
        v-if="!compactDesign && isLaptopOrDesktop"
        class="vx-col w-full sm:w-1/5 flex items-start pt-base">
        <label class="required">{{ $t('$General.Name') }}</label>
      </div>
      <div
        class="vx-col w-full"
        :class="{'sm:w-4/5': !compactDesign && isLaptopOrDesktop}">
        <vs-input
          v-model="nameLocal"
          :class="['w-full', compactDesign || !isLaptopOrDesktop ? 'required' : '']"
          :name="$t('$General.Name')"
          :label="compactDesign || !isLaptopOrDesktop ? $t('$General.Name') : ' '"
          :placeholder="$t('CampaignNamePlaceholder')"
          v-validate="'required|max:100'"
          data-vv-validate-on="blur|input"
          @input="$emit('update:name', nameLocal)"
          icon="icon icon-send"
          icon-pack="feather"
          :danger="errors.has($t('$General.Name'))"
          :danger-text="errors.first($t('$General.Name'))"
          val-icon-danger="clear"/>
      </div>
    </div>

    <campaign-information-from
      v-model="senderIdLocal"
      ref="campaignInformationFrom"
      :caller-id-type.sync="callerIdTypeLocal"
      :compact-design="compactDesign"
      :on-modal="onModal"
      :sender-business-name="senderBusinessNameLocal"
      :field-name="$t('$General.From')"
      :class="[compactDesign ? 'mt-6' : 'mt-base']"
      @is-valid="(val) => senderIdIsValid = val"
     />

    <div
      v-show="!isWaitingListMessage"
      class="vx-row"
      :class="[compactDesign ? 'mt-6': 'mt-base']">
      <div
        v-if="!compactDesign && isLaptopOrDesktop"
        class="vx-col sm:w-1/5 w-full flex items-start pt-base">
          <label class="required">
            <span class="inline-flex required">
              {{ $tc('$General.Type') }}

              <feather-icon
                icon="InfoIcon"
                class="cursor-pointer"
                svgClasses="h-4 w-4 ml-1"
                @click="showCampaignTypeModal=true"/>
            </span>
          </label>

      </div>
      <div
        class="vx-col w-full"
        :class="{'sm:w-4/5': !compactDesign && isLaptopOrDesktop}">
        <div
          class="vx-row"
          :class="{'mt-3': compactDesign}">
          <div class="vx-col w-full">
            <card-radio-toggle
              ref="messageTypeRadioToggle"
              :key="messageTypeRadioToggleKey"
              v-model="messageTypeLocal"
              :radio-name="$tc('$General.Type')"
              :radio-options="messageTypeCardRadioToggleOptions"
              :show-label="compactDesign || !isLaptopOrDesktop"
              icon="InfoIcon"
              required
              @icon-click="showCampaignTypeModal=true"/>

            <app-alert
              color="primary"
              :active="showReviewsSettingsAlert"
              icon-pack="feather"
              icon="icon-info"
              type="default-start"
              class="mt-6">
              <span>
                {{ $t('NoSetUpReviewsAlertText') }}
                <a
                  href="#"
                  class="link-cta ml-0"
                  @click.prevent="activeModalReviewSettings=true">
                  {{ $t('NoSetUpReviewsAlertLink') }}
                </a>
              </span>
            </app-alert>
          </div>
        </div>
      </div>
    </div>

    <slot></slot>

    <campaign-type-info-modal
      :style="onModal ? 'z-index: 53001 !important;' : ''"
      v-model="showCampaignTypeModal"/>

    <vs-popup
      :title="$t('$Components.$GoogleReviewsSummaryCard.SetupGoogleReviews')"
      :style="onModal ? 'z-index: 53001 !important;' : ''"
      fullscreen
      :active.sync="activeModalReviewSettings"
      class="mt-0 mb-0 pt-0 pb-0">
      <transition name="zoom-fade">
        <reviews-settings-create-or-edit
          v-if="activeModalReviewSettings"
          :card-style="false"
          :hide-request-review-button="true"
          @cancel="activeModalReviewSettings=false"
          @settings-updated="showReviewsSettingsAlert=false"/>
      </transition>
    </vs-popup>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import enums from '@/enums';

// Mixins
import campaignStep from '@/views/modules/campaigns/common/mixins/campaignStep';

// Components
import CardRadioToggle from '@/views/modules/_components/CardRadioToggle.vue';
import ReviewsSettingsCreateOrEdit from '@/views/modules/reviews/ReviewsSettingsCreateOrEdit.vue';
import CampaignInformationCampaignTypeInfoModal
from '@/views/modules/campaigns/components/campaign-information/CampaignInformationCampaignTypeInfoModal.vue';
import CampaignInformationFrom from '@/views/modules/campaigns/components/campaign-information/CampaignInformationFrom.vue';
import AppAlert from '@/views/_components/AppAlert.vue';
/**
 * Component to create or edit SMS Campaigns
 *
 * @module views/modules/campaigns/CampaignInformation
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'CampaignInformation',
  i18n: {
    messages: {
      en: {
        CampaignNamePlaceholder: 'Enter campaign name',
        NoSetUpReviewsAlertTitle: 'Enhance Your Campaigns',
        NoSetUpReviewsAlertText: 'Now you can include a shortened Google Review link in your transactional messages to boost your online reputation.',
        NoSetUpReviewsAlertLink: 'Start now.',
      },
    },
  },
  components: {
    CardRadioToggle,
    ReviewsSettingsCreateOrEdit,
    CampaignTypeInfoModal: CampaignInformationCampaignTypeInfoModal,
    CampaignInformationFrom,
    AppAlert,
  },
  mixins: [campaignStep],
  props: {
    name: {
      type: String,
      required: true,
    },
    messageType: {
      type: String,
      required: true,
      validator(type) {
        return ['', ...Object.values(enums.Campaign.MessageType)].indexOf(type) !== -1;
      },
    },
    senderId: {
      type: [Object, String],
      required: true,
    },
    senderBusinessName: {
      type: String,
      required: true,
    },
    callerIdType: {
      type: String,
      required: true,
      validator(type) {
        return Object.values(enums.Sender.Caller.Type).indexOf(type) !== -1;
      },
    },
    isWaitingListMessage: {
      type: Boolean,
      default: false,
    },
    onModal: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showReviewsSettingsAlert: false,
      nameLocal: this.name,
      messageTypeLocal: this.messageType,
      senderIdLocal: this.senderId,
      senderBusinessNameLocal: this.senderBusinessName,
      callerIdTypeLocal: this.callerIdType,
      showCampaignTypeModal: false,
      messageTypeCardRadioToggleOptions: [
        {
          value: this.$enums.Campaign.MessageType.TRANSACTIONAL,
          label: this.$t('$CampaignsModules.$CampaignType.TransactionalAndReviews'),
        },
        {
          value: this.$enums.Campaign.MessageType.MARKETING,
          label: this.$t('$CampaignsModules.$CampaignType.Marketing'),
        },
      ],

      activeModalReviewSettings: false,
      messageTypeRadioToggleKey: 0,

      senderIdIsValid: false,
    };
  },
  computed: {
    ...mapGetters({
      getTenantFlag: 'auth/getTenantFlag',
    }),
    isValid() {
      return !this.errors.any()
        && !!this.nameLocal
        && this.senderIdIsValid
        && !!this.messageTypeLocal;
    },
    isLaptopOrDesktop() {
      return this.$mq.includes(this.$enums.mqBreakpoints.LAPTOP)
        || this.$mq.includes(this.$enums.mqBreakpoints.DESKTOP);
    },
  },
  watch: {
    senderIdLocal(val) {
      this.$emit('update:sender-id', val || '');
    },
    senderBusinessNameLocal(val) {
      this.$emit('update:sender-business-name', val);
    },
    callerIdTypeLocal(val) {
      this.senderIdLocal = '';
      this.senderBusinessNameLocal = '';
      this.$emit('update:caller-id-type', val);
    },
    isValid(val) {
      this.$emit('validate', val);
    },
    messageType(val) {
      this.messageTypeLocal = val;
      this.messageTypeRadioToggleKey += 1;
    },
    messageTypeLocal: {
      immediate: true,
      handler(newVal, oldVal) {
        this.showReviewsSettingsAlert = !this.getTenantFlag('completedReviewsSettings') && newVal === this.$enums.Campaign.MessageType.TRANSACTIONAL;

        this.$emit('update:message-type', this.messageTypeLocal);

        if (oldVal !== undefined) {
          setTimeout(() => {
            this.$validator.validate(this.$tc('$General.Type'));
          }, 0);
        }
      },
    },
  },
  mounted() {
    this.$emit('validate', this.isValid);
  },
  methods: {
    validate() {
      const isValid = this.fieldsAreValid();
      if (this.$validator.errors.items.length > 0) {
        this.focusErrorField(this.$validator.errors.items[0].field);
      }
      return isValid;
    },
    fieldsAreValid() {
      const campaignInfoResult = this.$validator.validateAll();
      const messageTypeResult = this.$refs.messageTypeRadioToggle.$validator.validateAll();
      let result = campaignInfoResult && messageTypeResult;
      const fromResult = this.$refs.campaignInformationFrom.validate();

      result = result && fromResult;

      return result;
    },
    focusErrorField(fieldName) {
      const $fieldError = this.$el.querySelector(`[name="${fieldName}"]`);
      let $fieldToFocus = $fieldError;

      if ($fieldError) {
        if ($fieldError.attributes && $fieldError.attributes.type.value === 'hidden') {
          $fieldToFocus = $fieldError.parentElement;
          $fieldToFocus.scrollIntoView(false);
        } else {
          $fieldToFocus.focus();
        }
      }
    },
  },
  inject: {
    isCompact: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss">
  @media (max-width: 450px) {
    form {
      .vx-row.mt-base {
        .vx-col > {
          .v-select.w-full.vs--single.vs--searchable  {
            .vs__selected-options {
              .vs__search {
                margin-top: -32px !important;
              }
            }
          }

        .v-select.w-full.vs--open.vs--single.vs--searchable  {
            .vs__selected-options {
              .vs__search {
                margin-top: 4px !important;
              }
            }
          }
        }
      }
    }
  }
</style>
